import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewSectionContext } from '../../../utils/previewMapSections';
import { PlacedTeachingBlockCardType, TeachingBlockCardType } from '../types';
import styles from './PreviewMap.module.scss';
import { PreviewRow } from './previewMapRows';

export type WeekColumn = { key: string; week: number; year: number; label: string; holiday: boolean };
type Segment = { top: string; height: string; color?: string };

export type PreviewGridProps = {
  /** Zeilenart dieses Abschnitts – bestimmt Index-Schlüssel, Grid-Card-Auswahl und Segmenthöhe. */
  context: PreviewSectionContext;
  rows: PreviewRow[];
  /** Zeilen der Karte in der Hand → Hervorhebung. */
  pickedResources: Set<string>;
  weekColumns: WeekColumn[];
  /** Nur der erste Abschnitt trägt den Wochenkopf – darunter würde er nur Platz kosten. */
  showWeekHeader: boolean;
  cards: PlacedTeachingBlockCardType[];
  pickedCardUuid?: string;
  /** Fokus-Modus: gesteckte Karte im Rampenlicht, alle übrigen belegten Zellen abgedunkelt. */
  focusActive: boolean;
  /** Darstellung „Fachfarbe für Kärtchen"; kommt als Prop, weil dieser Baustein store-frei bleibt. */
  colorBySubject: boolean;
  /** Breite der Zeilennamen-Spalte – über alle Abschnitte identisch, sonst versetzen sich die Raster. */
  nameColumnWidth: string;
};

/**
 * Ein Abschnitt der Vorschau-Karte: das eigentliche Raster (Zeilen × Wochen) für genau einen
 * Zeilen-Kontext. Bewusst ohne Store-Zugriff, damit die Vorschau denselben Baustein mehrfach
 * (Klassen/Lehrende/Räume untereinander) rendern kann.
 */
export const PreviewGrid: FC<PreviewGridProps> = ({
  context,
  rows,
  pickedResources,
  weekColumns,
  showWeekHeader,
  cards,
  pickedCardUuid,
  focusActive,
  colorBySubject,
  nameColumnWidth,
}) => {
  const { t } = useTranslation();

  // Eigener Index je Vorschau-Kontext: `store.placedCardsByRowWeek` ist nach dem AKTUELLEN
  // Board-Kontext (gridContext) verschlüsselt und enthält daher keine Lehrer-/Raum-Zeilen, wenn
  // das Board auf Klassen steht. Hier bauen wir die Zuordnung unabhängig vom Board-Kontext auf –
  // über alle Wochen, die eine Epoche belegt (Block-Ausdehnung sichtbar).
  const cardsByRowWeek = useMemo(() => {
    const map = new Map<string, PlacedTeachingBlockCardType[]>();
    cards.forEach((card) => {
      const rowValues =
        context === 'teachers'
          ? card.teachers.map((teacher) => teacher.uuid)
          : context === 'rooms'
            ? card.rooms.map((room) => room.uuid)
            : card.classes.map((schoolClass) => schoolClass.uuid);
      if (rowValues.length === 0) return;
      const end = dayjs(card.endDate);
      for (
        let cur = dayjs(card.startDate).startOf('isoWeek');
        cur.isSameOrBefore(end, 'day');
        cur = cur.add(1, 'week')
      ) {
        const week = cur.isoWeek();
        const year = cur.isoWeekYear();
        rowValues.forEach((rowValue) => {
          const key = `${rowValue}:${week}:${year}`;
          const bucket = map.get(key);
          if (bucket) bucket.push(card);
          else map.set(key, [card]);
        });
      }
    });
    return map;
  }, [cards, context]);

  const gridCardForRow = (card: TeachingBlockCardType, rowUuid: string) => {
    const gridCard =
      context === 'classes'
        ? card.classGridCards.find((c) => c.classUuid === rowUuid)
        : context === 'teachers'
          ? card.teacherGridCards.find((teacher) => teacher.teacherUuid === rowUuid)
          : card.roomGridCards.find((room) => room.roomUuid === rowUuid);
    // Farbe (Fach-Gradient) ist über alle Grid-Card-Typen identisch; fehlt sie für die Zeile,
    // greift der Fallback, damit die Karte trotzdem farbig (nicht grau) ist – wie im Hauptboard.
    const colorCard = gridCard?.cssGradientString
      ? gridCard
      : (card.classGridCards[0] ?? card.teacherGridCards[0] ?? card.roomGridCards[0]);
    return { gridCard, colorCard };
  };

  const cellInfo = (
    rowUuid: string,
    column: WeekColumn,
  ): { segments: Segment[]; count: number; label: string; hasPicked: boolean } => {
    const cellCards = cardsByRowWeek.get(`${rowUuid}:${column.week}:${column.year}`);
    if (!cellCards || cellCards.length === 0) return { segments: [], count: 0, label: '', hasPicked: false };

    const segments: Segment[] = [];
    const labels: string[] = [];
    let hasPicked = false;
    cellCards.forEach((card) => {
      if (card.uuid === pickedCardUuid) hasPicked = true;
      const { gridCard, colorCard } = gridCardForRow(card, rowUuid);
      // Bei „Fachfarbe für Kärtchen" gewinnt die Fachfarbe; Position und Höhe (Teilung) bleiben.
      const subjectColor = colorBySubject ? (card.subject?.timetableConfig?.color ?? null) : null;
      segments.push({
        top: context === 'rooms' ? '0%' : (gridCard?.cssTop ?? '0%'),
        height: context === 'rooms' ? '100%' : (gridCard?.cssHeight ?? '100%'),
        color: subjectColor ?? colorCard?.cssGradientString ?? undefined,
      });
      const teachers = card.teachers
        .map((teacher) => teacher.shortName)
        .filter(Boolean)
        .join(', ');
      const classes = card.classes
        .map((schoolClass) => schoolClass.shortName)
        .filter(Boolean)
        .join(', ');
      const label = [card.subject?.shortName, classes, teachers].filter(Boolean).join(' · ');
      if (label) labels.push(label);
    });

    return { segments, count: cellCards.length, label: labels.join(' | '), hasPicked };
  };

  if (rows.length === 0) return <div className={styles.empty}>{t('pinboard.previewMap.empty')}</div>;

  return (
    <div
      className={styles.grid}
      style={{ gridTemplateColumns: `${nameColumnWidth} repeat(${weekColumns.length}, minmax(6px, 1fr))` }}
    >
      {showWeekHeader && (
        <>
          <div className={styles.corner} />
          {weekColumns.map((column, index) => (
            <div
              key={`week-${column.key}-${index}`}
              className={classNames(styles.day, { [styles.holiday]: column.holiday })}
              title={`KW ${column.label}`}
            >
              {column.label}
            </div>
          ))}
        </>
      )}

      {rows.map((row) => (
        <Fragment key={row.uuid}>
          <div
            className={classNames(styles['row-name'], { [styles['picked-row']]: pickedResources.has(row.uuid) })}
            title={row.name}
          >
            {row.name}
          </div>
          {weekColumns.map((column, index) => {
            const { segments, count, label, hasPicked } = cellInfo(row.uuid, column);
            const occupied = count > 0;
            return (
              <div
                key={`${row.uuid}-${column.key}-${index}`}
                className={classNames(styles.cell, {
                  [styles.occupied]: occupied,
                  [styles['picked-row']]: pickedResources.has(row.uuid),
                  [styles['picked-cell']]: hasPicked,
                  [styles.dimmed]: focusActive && occupied && !hasPicked,
                })}
                data-cards={occupied ? `${row.name} — KW ${column.label}: ${label}` : undefined}
              >
                {segments.map((segment, segmentIndex) => (
                  <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={segmentIndex}
                    className={styles.segment}
                    style={{ top: segment.top, height: segment.height, background: segment.color }}
                  />
                ))}
                {/* Ferien-Band über der Karte (höherer z-index) → wirkt wie die Ferienlücke im Board. */}
                {column.holiday && <span className={styles['holiday-overlay']} />}
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
};

PreviewGrid.displayName = 'TeachingBlockPreviewGrid';
